// @ts-check
/**
 * Time menu: batteries-included date/range selector to go inside
 * mp-drop-menu
 */

import {FEATURE_GATES_UNLIMITED, MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT} from '../../../report/constants';
import {SELECTOR_TYPE} from '../constants';
import {pluralize} from '../../../util';
import {formatDate, parseDate} from '../../../util/date';
import {registerMPElement} from 'common/util/register-element';
import DateRange from '../../../widgets/date-range-picker/models/date-range';
import {DateRangeType, DisplayContext} from '../../../widgets/date-range-picker/models/types';
import {DATE_RANGE_TYPES} from '../../../widgets/date-range-picker/models/date-range-factory';

import {ChildMenu} from '../child-menu';
import template from './index.jade';

const CUSTOM_MENU_ITEM = Object.freeze({
  hasCaret: true,
  label: `Select a date range...`,
  value: `custom`,
});

const DATE_RANGE_PREFIX = Object.freeze({
  [DateRangeType.Between]: `from`,
  [DateRangeType.On]: `on`,
  [DateRangeType.RelativeAfter]: `in the`,
  [DateRangeType.Since]: `since`,
});

class TimeMenu extends ChildMenu {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'display-context': {type: `string`, default: DisplayContext.FullLength},
      'selector-type': {type: `string`, default: SELECTOR_TYPE.TEXT, enum: Object.values(SELECTOR_TYPE)},
    };
  }

  get config() {
    return {
      template,

      defaultState: {
        DisplayContext,
        inCustomRange: null,
      },

      helpers: {
        ...super.config.helpers,
        getMenuSections: () => this.getMenuSections(),
        selectDateRange: () => this.onDateRangeSelected(this.state.stagedDateRange),
        getDateRangeAttributes: () => this.state.stagedDateRange.getAttributes(),
        updateStagedDateRange: ({detail: {dateRange}}) => this.update({stagedDateRange: dateRange}),
        onPaneBackClick: () => this.update({inCustomRange: false}),
        onPresetItemClick: (ev) => this.onPresetItemClick(ev.detail.item),
        getUpsellAttrs: () => {
          const maxDataHistoryDays = this.attr(`max-data-history-days`);
          if (maxDataHistoryDays > 0 && maxDataHistoryDays !== FEATURE_GATES_UNLIMITED) {
            return {
              'show-upsell': maxDataHistoryDays < MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT,
              'max-history-days': maxDataHistoryDays,
            };
          }
          return {};
        },
        isStagedDateRangeValid: () => {
          const dateRange = this.state.stagedDateRange;
          const maxDataHistoryDays = this.attr(`max-data-history-days`);
          return dateRange.isValid(maxDataHistoryDays);
        },
      },
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);

    if (attr === `open` && !this.attr(`open`)) {
      this.update({
        inCustomRange: false,
        stagedDateRange: this.state.selected,
      });
    }
  }

  get dateRangeClass() {
    return DATE_RANGE_TYPES[this.attr(`display-context`)];
  }

  handleLabelClick() {
    let stagedDateRange = this.state.selected;

    // prevents invalid date ranges (i.e. hour ranges on a full length
    // date range) from reaching the date range selection component
    if (!stagedDateRange.isValid()) {
      stagedDateRange = new this.dateRangeClass();
    }

    this.update({
      inCustomRange: !this.getPresetMatch(),
      stagedDateRange,
    });
  }

  getMenuSections() {
    const {stagedDateRange} = this.state;

    if (!stagedDateRange) {
      return [];
    }

    const maxDataHistoryDays = this.attr(`max-data-history-days`);
    const matchedPreset = this.getPresetMatch();
    const presetOptions = this.dateRangeClass.presetOptions
      .filter((option) =>
        maxDataHistoryDays ? new DateRange(option.value).getIntervalDays() <= maxDataHistoryDays : true,
      )
      .map((option) => {
        return {
          isSelected: matchedPreset && matchedPreset.label === option.label,
          label: option.label,
          value: option.value,
        };
      });

    const customItem = {
      ...CUSTOM_MENU_ITEM,
      isSelected: !matchedPreset,
    };

    return [
      {
        items: [...presetOptions, customItem],
      },
    ];
  }

  onPresetItemClick(item) {
    if (item.value === CUSTOM_MENU_ITEM.value) {
      const {stagedDateRange} = this.state;
      const defaultRange = new this.dateRangeClass();

      this.update({
        inCustomRange: true,
        stagedDateRange: stagedDateRange.isValid() ? stagedDateRange : defaultRange,
      });
    } else {
      this.onDateRangeSelected(item.value);
    }
  }

  getPrefixLabel() {
    const {selected} = this.state;
    const matchedPreset = this.getPresetMatch();
    return DATE_RANGE_PREFIX[matchedPreset ? DateRangeType.RelativeAfter : selected.type];
  }

  // looks for a matched date selection in the menu
  getPresetMatch() {
    const {selected} = this.state;

    if (!selected) {
      return null;
    }

    return this.dateRangeClass.presetOptions.find((option) => {
      return selected.equals(new DateRange(option.value));
    });
  }

  onDateRangeSelected(dateRange) {
    this.dispatchEvent(new CustomEvent(`select`, {detail: dateRange}));
    this.trackSelection(this.trackingProps(dateRange));
  }

  selectedLabel() {
    const matchedPreset = this.getPresetMatch();

    if (!this.state.selected) {
      return null;
    }

    if (matchedPreset) {
      return matchedPreset.label;
    } else {
      const {window, type, rolling} = this.state.selected;
      const {from, to} = this.state.selected.getCalendarDates();
      const getFormattedDate = (val) => formatDate(parseDate(val));
      const rollingSuffix = rolling ? ` (rolling)` : ``;

      switch (type) {
        case DateRangeType.Between:
          return `${getFormattedDate(from)} — ${getFormattedDate(to)}${rollingSuffix}`;
        case DateRangeType.On:
          return `${getFormattedDate(from)}${rollingSuffix}`;
        case DateRangeType.Since:
          return this.attr(`selector-type`) === `text`
            ? `${getFormattedDate(from)}`
            : `Since ${getFormattedDate(from)}`;
        case DateRangeType.RelativeAfter: {
          const {value, unit} = window;
          return `Last ${value > 1 ? `${value} ` : ``}${pluralize(unit, value)}`;
        }
        default:
          throw new Error(`unsupported type`);
      }
    }
  }

  transformSelectedVal(val) {
    return new this.dateRangeClass(val);
  }

  isContentSearchable() {
    return false;
  }

  trackingProps(dateRange) {
    return {
      eventName: `Select date range`,
      props: {
        'date range from': dateRange.from || null,
        'date range to': dateRange.to || null,
        'date range type': dateRange.type,
        'window unit': dateRange.window ? dateRange.window.unit : null,
        'window value': dateRange.window ? dateRange.window.value : null,
      },
    };
  }
}

export default registerMPElement(`mp-time-menu`, TimeMenu);
